import { useMemo, useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { Settings as SettingsIcon, Bell, UserPlus, MoreVertical, Pencil, LogOut } from 'lucide-react'
import { ApiError } from '@/shared/api/client'
import { errorMessage } from '@/shared/i18n/errorMessage'
import { useTranslation } from '@/shared/i18n/useTranslation'
import { AppHeader } from '@/shared/components/layout/AppHeader'
import { Button } from '@/shared/components/ui/Button'
import { InfoButton } from '@/shared/components/ui/InfoButton'
import { GameweekNavigator } from '@/features/league/components/GameweekNavigator'
import {
  useCompetitionGameweeks,
  useLeaveSurvivorGame,
  useMySurvivorPicks,
  useSurvivorGame,
  useSurvivorHallOfFame,
  useSurvivorPicks,
  useSurvivorStandings,
} from '../queries'
import { resolveRoundBrowsing } from '../lib/roundBrowsing'
import { SurvivorGameStatus, type SurvivorGameDetail } from '../types'
import { GameStatusCard } from './GameStatusCard'
import { SurvivorRulesSheet } from './SurvivorRulesSheet'
import { SurvivorPickPanel } from './SurvivorPickPanel'
import { SurvivorPeerPicks } from './SurvivorPeerPicks'
import { SurvivorStandingsCard } from './SurvivorStandingsCard'
import { SurvivorHistory } from './SurvivorHistory'
import { SurvivorHallOfFame } from './SurvivorHallOfFame'
import { SurvivorSeasonBanner } from './SurvivorSeasonBanner'
import { EditSurvivorTeamNameSheet } from './EditSurvivorTeamNameSheet'
import { SurvivorInviteSheet } from './SurvivorInviteSheet'
import { SurvivorNotificationsSheet } from './SurvivorNotificationsSheet'
import styles from './SobreviventeGamePage.module.css'

type Props = { gameId: string; gw: string | undefined }

export function SobreviventeGamePage({ gameId, gw }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const gameQuery = useSurvivorGame(gameId)

  function goBack() {
    void navigate({ to: '/sobrevivente' })
  }

  // ── Error states ────────────────────────────────────────────────────────────
  if (gameQuery.error instanceof ApiError) {
    return (
      <div className={styles.page}>
        <AppHeader title={t('survivor.game.title')} onBack={goBack} />
        <div className={styles.empty}>
          <h2 className={styles.emptyHeading}>{t('survivor.game.notFound')}</h2>
          <p className={styles.emptyDescription}>
            {errorMessage(gameQuery.error, t, 'error.survivor.load')}
          </p>
          <Button variant="secondary" onClick={goBack}>{t('common.back')}</Button>
        </div>
      </div>
    )
  }

  if (gameQuery.isPending || !gameQuery.data) {
    return (
      <div className={styles.page}>
        <AppHeader title={t('common.loading')} onBack={goBack} />
      </div>
    )
  }

  const game = gameQuery.data
  return <GameContent game={game} gw={gw} onBack={goBack} />
}

// ─────────────────────────────────────────────────────────────────────────────
// All the data-bound rendering is in here, so we can rely on a loaded `game`.

function GameContent({
  game,
  gw,
  onBack,
}: {
  game: ReturnType<typeof useSurvivorGame>['data'] & object
  gw: string | undefined
  onBack: () => void
}) {
  const navigate = useNavigate()

  const { data: gameweeks }  = useCompetitionGameweeks(game.competitionId)
  const { data: standings }  = useSurvivorStandings(game.id)
  const { data: myPicks }    = useMySurvivorPicks(game.id)
  // Only a game that has been restarted has a hall of fame to show, and only that game pays
  // for the request. `seasonNumber > 1` is true for every season after the first, and a first
  // season that has since been restarted links forward from the banner instead.
  const { data: seasons }    = useSurvivorHallOfFame(game.id, game.seasonNumber > 1)

  // Which round the page opens on and how far forward the browser may walk. The rule lives in
  // roundBrowsing.ts because the finished-game case is the one that was wrong, and it is worth
  // pinning in a test rather than re-deriving inside a component.
  const { currentGwNumber: currentNumber, maxGwNumber: maxNumber } = useMemo(
    () => resolveRoundBrowsing(gameweeks ?? [], game, myPicks ?? []),
    [gameweeks, game, myPicks],
  )

  const currentGw = gameweeks?.find((g) => g.number === currentNumber)

  const selectedGwNumber = (() => {
    if (gw) {
      const n = parseInt(gw, 10)
      if (Number.isFinite(n)) return n
    }
    return currentGw?.number ?? 1
  })()

  const selectedGw = gameweeks?.find((g) => g.number === selectedGwNumber)
  const totalGws   = gameweeks?.length ?? 0
  const currentGwNumber = currentNumber ?? 1
  const maxGwNumber     = maxNumber ?? currentGwNumber

  function handleGwPrev() {
    if (selectedGwNumber <= 1) return
    void navigate({
      to:      '/sobrevivente/$gameId',
      params:  { gameId: game.id },
      search:  { gw: String(selectedGwNumber - 1) },
      replace: true,
    })
  }

  function handleGwNext() {
    if (selectedGwNumber >= Math.min(totalGws, maxGwNumber)) return
    void navigate({
      to:      '/sobrevivente/$gameId',
      params:  { gameId: game.id },
      search:  { gw: String(selectedGwNumber + 1) },
      replace: true,
    })
  }

  const { data: peerPicks } = useSurvivorPicks(game.id, selectedGw?.id)

  // Submission allowed only when:
  //  - game is REGISTRATION_OPEN or ACTIVE
  //  - caller is alive
  //  - selected GW = current GW (you can only pick for the upcoming round)
  //  - the GW is not locked
  // Lock is driven by GW STATUS (the admin Lock action), not the clock — this mirrors
  // the backend gate Gameweek.IsEntryLocked(). Manual-lock mode means a Reset that returns
  // a GW to ACTIVE reopens picks even though the kickoff time has passed. See
  // .claude/plans/clock-triggers.md (#2).
  const isCurrentGw = selectedGw?.id === currentGw?.id
  const isGwLocked  = !selectedGw
    || selectedGw.status === 'LOCKED'
    || selectedGw.status === 'FINALIZED'
  const canPick =
    !isGwLocked
    && isCurrentGw
    && game.isAlive
    && game.status !== SurvivorGameStatus.Finished

  return (
    <div className={styles.page}>
      <AppHeader title={game.name} onBack={onBack} actions={<GameActions game={game} />} />

      <div className={styles.content}>
        <GameStatusCard game={game} />

        <SurvivorSeasonBanner game={game} />

        <div className={styles.gwNavigatorWrap}>
          <GameweekNavigator
            selectedGwNumber={selectedGwNumber}
            currentGwNumber={currentGwNumber}
            maxGwNumber={maxGwNumber}
            totalGws={totalGws || 1}
            onPrev={handleGwPrev}
            onNext={handleGwNext}
          />
        </div>

        {selectedGw && (
          <SurvivorPickPanel
            gameId={game.id}
            gameweekId={selectedGw.id}
            gameweekNumber={selectedGw.number}
            canPick={canPick}
            isCurrentGw={isCurrentGw}
            isAlive={game.isAlive}
            isGameFinished={game.status === SurvivorGameStatus.Finished}
            myPicks={myPicks ?? []}
          />
        )}

        {selectedGw && (
          <SurvivorPeerPicks
            picks={peerPicks ?? []}
            isLocked={isGwLocked}
            gameweekNumber={selectedGw.number}
          />
        )}

        <div className={styles.bottomGrid}>
          {standings && <SurvivorStandingsCard standings={standings} />}
          <SurvivorHistory picks={myPicks ?? []} />
        </div>

        {seasons && <SurvivorHallOfFame seasons={seasons} currentGameId={game.id} />}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * The game's own header controls, handed to `AppHeader` as `actions`.
 *
 * This page used to render a whole parallel header — its own back button, title,
 * avatar dropdown and logout. That duplicate drifted from the real one (it had no
 * preferences control, and a different logout label), so it is gone: only the
 * game-specific buttons live here now.
 */
function GameActions({ game }: { game: SurvivorGameDetail }) {
  const { t } = useTranslation()
  const [showRules, setShowRules]                 = useState(false)
  const [showInvite, setShowInvite]               = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showEditName, setShowEditName]           = useState(false)

  return (
    <>
      <InfoButton label={t('survivor.game.rules')} onClick={() => setShowRules(true)} />

      <button
        type="button"
        className={styles.iconBtn}
        onClick={() => setShowInvite(true)}
        aria-label={t('survivor.game.invite')}
      >
        <UserPlus size={18} aria-hidden="true" />
      </button>

      <button
        type="button"
        className={styles.iconBtn}
        onClick={() => setShowNotifications(true)}
        aria-label={t('survivor.game.notifications')}
      >
        <Bell size={18} aria-hidden="true" />
      </button>

      <OverflowMenu game={game} onEditTeamName={() => setShowEditName(true)} />

      {showRules && <SurvivorRulesSheet onClose={() => setShowRules(false)} />}
      {showInvite && <SurvivorInviteSheet game={game} onClose={() => setShowInvite(false)} />}
      {showNotifications && <SurvivorNotificationsSheet onClose={() => setShowNotifications(false)} />}
      {showEditName && (
        <EditSurvivorTeamNameSheet
          gameId={game.id}
          currentName={game.myTeamName ?? ''}
          onClose={() => setShowEditName(false)}
        />
      )}
    </>
  )
}

// ── Overflow menu: edit team name · settings (owner) · leave ──────────────────

function OverflowMenu({ game, onEditTeamName }: { game: SurvivorGameDetail; onEditTeamName: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen]               = useState(false)
  const [confirmLeave, setConfirmLeave] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const leave   = useLeaveSurvivorGame()

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
        setConfirmLeave(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Alias freezes once the game leaves registration (matches the backend guard).
  const teamNameLocked = game.status !== SurvivorGameStatus.RegistrationOpen
  // The owner can't leave — they must transfer ownership or delete in settings.
  const canLeave = !game.isOwner

  function handleLeave() {
    if (!confirmLeave) {
      setConfirmLeave(true)
      return
    }
    leave.mutate(game.myParticipationId, {
      onSuccess: () => void navigate({ to: '/sobrevivente' }),
    })
  }

  return (
    <div className={styles.overflowWrap} ref={wrapRef}>
      <button
        className={styles.iconBtn}
        onClick={() => setOpen((v) => !v)}
        aria-label={t('survivor.game.moreOptions')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <MoreVertical size={18} />
      </button>

      {open && (
        <div className={styles.dropdown} role="menu">
          <button
            className={styles.dropdownItem}
            role="menuitem"
            disabled={teamNameLocked}
            title={teamNameLocked ? t('survivor.game.teamNameLocked') : undefined}
            onClick={() => { setOpen(false); onEditTeamName() }}
          >
            <Pencil size={15} />
            {t('survivor.game.editTeamName')}
          </button>

          {game.isOwner && (
            <Link
              to="/sobrevivente/$gameId/settings"
              params={{ gameId: game.id }}
              className={styles.dropdownItem}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              <SettingsIcon size={15} />
              {t('survivor.game.settingsMenu')}
            </Link>
          )}

          <button
            className={`${styles.dropdownItem} ${styles.dropdownLeave}`}
            role="menuitem"
            disabled={!canLeave || leave.isPending}
            title={!canLeave ? t('survivor.game.leaveBlocked') : undefined}
            onClick={handleLeave}
          >
            <LogOut size={15} />
            {confirmLeave ? t('survivor.game.leaveConfirm') : t('survivor.game.leave')}
          </button>
        </div>
      )}
    </div>
  )
}
